/**
 * Tags (`040-api-contract.md` → Tags, `070-search-and-links.md` §6). **Reads only.**
 *
 * Same rule as `pages.ts`: there are no write functions in this file and there must not
 * be. Every tag mutation is an outbox entry (`features/tags/useTags.ts`) — a `postTag()`
 * helper here would be the one call site that forgets the queue.
 */
import { apiFetch } from '@/api/client';
import type { TreePage } from '@/api/endpoints/pages';

export interface Tag {
  id: string;
  name: string;
  /** Optional `#rrggbb`. `null` renders with the neutral chip style; never a semantic. */
  color: string | null;
  /** How many pages carry it. Rendered in the sidebar; recomputed server-side. */
  pageCount: number;
}

export interface TagsResponse {
  tags: Tag[];
}

/** `GET /api/tags/{id}/pages` → tree-shaped items, rendered as a FLAT list (`070` §6). */
export interface TagPagesResponse {
  pages: TreePage[];
}

export function getTags(): Promise<TagsResponse> {
  return apiFetch<TagsResponse>('/api/tags');
}

export function getTagPages(id: string): Promise<TagPagesResponse> {
  return apiFetch<TagPagesResponse>(`/api/tags/${id}/pages`);
}

/* ── Write bodies (the shapes the outbox carries) ───────────────────────────── */

export interface CreateTagBody {
  /**
   * Client-generated ULID, exactly as for a page (`030-data-model.md`: the `tag` PK is a
   * ULID, and `040`'s create shape is the same family as `POST /api/pages`).
   *
   * It is not a nicety. Assignment is `PATCH /api/pages/{id} { tagIds }` — the FULL set —
   * so the moment a user presses Enter on "Create tag 'npc'" the client must already know
   * the id it is about to put in that set. Waiting for the server to mint one would mean a
   * raw `fetch` outside the outbox, which is the one unforgivable bug in this codebase
   * (`112-offline-drafts.md` §5). The outbox's FIFO order guarantees the `tag.create`
   * lands before the `page.update` that references it.
   */
  id: string;
  name: string;
  color?: string | null;
}

export interface UpdateTagBody {
  name?: string;
  color?: string | null;
}

/**
 * Case-insensitive per owner (`UNIQUE (owner_id, lower(name))`). Typing `NPC` when `npc`
 * exists REUSES `npc` — it is not a second tag and it is not an error. The client resolves
 * this before it ever offers "Create tag" (`070` §6).
 */
export function findTagByName(tags: readonly Tag[], name: string): Tag | undefined {
  const needle = name.trim().toLowerCase();
  return tags.find((tag) => tag.name.toLowerCase() === needle);
}

/** 1–64 chars, trimmed, not only whitespace (`070` §6, "Name rules"). */
export function isValidTagName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length >= 1 && trimmed.length <= 64;
}
